import React, { useState, useEffect, useMemo } from "react";
import AutoSizer from "react-virtualized-auto-sizer";
import { FixedSizeList as List } from "react-window";
import { useDispatch } from "react-redux";

// Material UI
import { Box, Grid, MenuItem, Button, TextField } from "@mui/material";
import { Search } from "@mui/icons-material";

// Custom Components
import LoadingSpinner from "../../components/LoadingSpinner/LoadingSpinner";
import DualList from "../../components/DualList/DualList";
import AdminPageLayout from "../../components/FullPageLayout/AdminPageLayout";
import Breadcrumb from "../../components/Breadcrumb/Breadcrumb";
import ButtonGroup from "../../components/Buttons/ButtonGroup";
import {
  enqueueSuccessSnackbar,
  enqueueInfoSnackbar,
  closeSnackbar
} from "../../redux/actions/snackbarActions";
// Hooks
import useScopedTranslation from "../../hooks/useScopedTranslation";
import { useUsers } from "../../hooks/apiHooks/users";
import { useLocations } from "../../hooks/apiHooks/locations";
import { usePositions } from "../../hooks/apiHooks/positions";
import { useGroups } from "../../hooks/apiHooks/groups";
import useResource from "../../hooks/useResource";

//Constants
import { states } from "../../constants";

const USER_JSONAPI_REQUEST = {
  fields: {
    user: ["fullName", "location", "position", "supervisedGroups"],
    location: ["name"],
    position: ["name"],
    group: ["name"]
  },
  include: ["location", "position", "supervisedGroups"]
};

const FILTERED_USERS_JSONAPI_REQUEST = {
  fields: {
    user: ["fullName"]
  },
  sort: "fullName"
};

const locationsToStates = locations => {
  const locationStates = new Set();
  locations.forEach(location => locationStates.add(location.state));
  return Array.from(locationStates)
    .sort()
    .map(stateId => states.find(state => state.value === stateId))
    .filter(state => !!state)
    .map(state => ({ name: state.label, id: state.value }));
};

const Filters = ({ onFilter, group }) => {
  const [term, setTerm] = useState("");
  const [location, setLocation] = useState(null);
  const [position, setPosition] = useState(null);
  const [state, setState] = useState(null);

  const [locations, setLocations] = useState([]);
  const [states, setStates] = useState([]);
  const [positions, setPositions] = useState([]);

  const { getLocations } = useLocations();
  const { getPositions } = usePositions();

  const t = useScopedTranslation("groups.leaders.filters");

  useEffect(() => {
    getLocations({
      filters: { active: true, divisionId: group?.customer?.id },
      fields: { location: ["name", "state"] },
      sort: "name"
    }).then(response => {
      const locations = response.data;
      setLocations(locations);
      setStates(locationsToStates(locations));
    });
    getPositions({
      filters: { active: true, divisionId: group?.customer?.id },
      fields: { position: ["name"] },
      sort: "name"
    }).then(response => {
      const positions = response.data;
      setPositions(positions);
    });
  }, []);

  const setFilters = () => {
    onFilter({
      term,
      location,
      position,
      state
    });
  };

  const clearFilters = () => {
    onFilter({});
    setTerm("");
    setLocation(null);
    setPosition(null);
    setState(null);
  };

  return (
    <Grid container spacing={2} sx={{ alignItems: "center", pb: 2 }}>
      <Grid item xs={12} md={4}>
        <TextField
          fullWidth
          value={term}
          onChange={event => {
            setTerm(event.target.value);
          }}
          variant="filled"
          InputProps={{
            disableUnderline: true,
            endAdornment: (
              <Button onClick={setFilters}>
                {" "}
                <Search color="action" />
              </Button>
            ),
            onKeyDown: ({ key }) => key === "Enter" && setFilters(),
            inputProps: { "data-qa": "validate-search" }
          }}
          label={t("fullName.label")}
        />
      </Grid>
      <Grid item xs={12} md={4}>
        <TextField
          select
          fullWidth
          name="location"
          value={location}
          onChange={e => setLocation(e.target.value)}
          variant="filled"
          label={t("locations.label")}
          InputProps={{
            disableUnderline: true,
            onKeyDown: ({ key }) => key === "Enter" && setFilters()
          }}
        >
          <MenuItem value={null}>{t("locations.all")}</MenuItem>
          {locations.map(location => (
            <MenuItem value={location.id} key={location.id}>
              {location.name}
            </MenuItem>
          ))}
        </TextField>
      </Grid>
      <Grid item xs={12} md={4}>
        <TextField
          select
          fullWidth
          name="state"
          value={state}
          variant="filled"
          onChange={e => setState(e.target.value)}
          label={t("states.label")}
          InputProps={{
            disableUnderline: true,
            onKeyDown: ({ key }) => key === "Enter" && setFilters()
          }}
        >
          <MenuItem value={null}>{t("states.all")}</MenuItem>
          {states.map(state => (
            <MenuItem value={state.id} key={state.id}>
              {state.name}
            </MenuItem>
          ))}
        </TextField>
      </Grid>
      <Grid item xs={12} md={4}>
        <TextField
          select
          fullWidth
          name="position"
          value={position}
          variant="filled"
          onChange={e => setPosition(e.target.value)}
          label={t("positions.label")}
          InputProps={{
            disableUnderline: true,
            onKeyDown: ({ key }) => key === "Enter" && setFilters()
          }}
        >
          <MenuItem value={null}>{t("positions.all")}</MenuItem>
          {positions.map(position => (
            <MenuItem value={position.id} key={position.id}>
              {position.name}
            </MenuItem>
          ))}
        </TextField>
      </Grid>
      <Grid item xs={12} md={8}>
        <ButtonGroup>
          <Button color="primary" onClick={clearFilters}>
            {t("clear")}
          </Button>

          <Button variant="contained" color="primary" onClick={setFilters}>
            {t("filter")}
          </Button>
        </ButtonGroup>
      </Grid>
    </Grid>
  );
};

const recalculateLists = (
  filteredUsers,
  selectedIdUserMap,
  unselectedIdUserMap,
  setSelectedList,
  setUnSelectedList
) => {
  const selectedList = [];
  const unselectedList = [];
  filteredUsers.forEach(user => {
    if (selectedIdUserMap.has(user.id)) {
      selectedList.push(selectedIdUserMap.get(user.id));
    } else if (unselectedIdUserMap.has(user.id)) {
      unselectedList.push(unselectedIdUserMap.get(user.id));
    }
  });
  setSelectedList(selectedList);
  setUnSelectedList(unselectedList);
};

const GroupLeaders = ({ group, goBack }) => {
  const [allUsers, setAllUsers] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [selectedIdUserMap, setSelectedIdUserMap] = useState(null);
  const [unselectedIdUserMap, setUnselectedIdUserMap] = useState(null);
  const [selectedList, setSelectedList] = useState([]);
  const [unselectedList, setUnSelectedList] = useState([]);
  const [filters, setFilters] = useState({
    active: true,
    divisionId: group?.customer?.id
  });

  const t = useScopedTranslation("groups.leaders");

  const dispatch = useDispatch();

  const { getUsers } = useUsers();
  const { updateGroup } = useGroups();

  const apiCallbacks = useMemo(
    () => ({
      getUsers: (...params) => getUsers(...params)
    }),
    []
  );
  const [
    { data: filteredUsers },
    { getUsers: search },
    filterAction
  ] = useResource(apiCallbacks);

  useEffect(() => {
    if (allUsers !== null) {
      search({ filters, ...FILTERED_USERS_JSONAPI_REQUEST });
    }
  }, [allUsers, filters]);

  useEffect(() => {
    if (filteredUsers) {
      recalculateLists(
        filteredUsers,
        selectedIdUserMap,
        unselectedIdUserMap,
        setSelectedList,
        setUnSelectedList
      );
    }
  }, [filteredUsers]);

  useEffect(() => {
    if (!group || !group.id) {
      return;
    }
    (async () => {
      const { data: users } = await getUsers({
        filters: { active: true, divisionId: group.customer?.id },
        ...USER_JSONAPI_REQUEST
      });

      const selectedIdsMap = new Map();
      const unselectedIdsMap = new Map();
      users.forEach(user => {
        const mappedUser = {
          id: user.id,
          name: user.fullName,
          subtitles: [user.position.name, user.location.name]
        };
        if (
          user.supervisedGroups.find(userGroup => userGroup.id === group.id)
        ) {
          selectedIdsMap.set(mappedUser.id, mappedUser);
        } else {
          unselectedIdsMap.set(mappedUser.id, mappedUser);
        }

        setSelectedIdUserMap(selectedIdsMap);
        setUnselectedIdUserMap(unselectedIdsMap);
      });

      setAllUsers(users);
    })();
  }, [(group || {}).id]);

  const loading = allUsers === null;
  const isUpdating = isSaving || filterAction !== null;

  const handleAddAll = () => {
    unselectedList.forEach(item => {
      unselectedIdUserMap.delete(item.id);
      selectedIdUserMap.set(item.id, item);
    });
    recalculateLists(
      filteredUsers,
      selectedIdUserMap,
      unselectedIdUserMap,
      setSelectedList,
      setUnSelectedList
    );
  };

  const handleRemoveAll = () => {
    selectedList.forEach(item => {
      selectedIdUserMap.delete(item.id);
      unselectedIdUserMap.set(item.id, item);
    });
    recalculateLists(
      filteredUsers,
      selectedIdUserMap,
      unselectedIdUserMap,
      setSelectedList,
      setUnSelectedList
    );
  };

  const handleAdd = item => {
    unselectedIdUserMap.delete(item.id);
    selectedIdUserMap.set(item.id, item);
    recalculateLists(
      filteredUsers,
      selectedIdUserMap,
      unselectedIdUserMap,
      setSelectedList,
      setUnSelectedList
    );
  };

  const handleRemove = item => {
    selectedIdUserMap.delete(item.id);
    unselectedIdUserMap.set(item.id, item);
    recalculateLists(
      filteredUsers,
      selectedIdUserMap,
      unselectedIdUserMap,
      setSelectedList,
      setUnSelectedList
    );
  };

  const applyFilters = filters => {
    setFilters({
      ...(filters.term && { fullName: filters.term }),
      ...(filters.position && { positions: filters.position }),
      ...(filters.location && { locations: filters.location }),
      ...(filters.state && { states: filters.state }),
      divisionId: group.customer?.id,
      active: true
    });
  };

  const save = () => {
    setIsSaving(true);
    let waitingSnackbar = dispatch(
      enqueueInfoSnackbar({
        message: t("messages.savingWaitMsg"),
        options: { autoHideDuration: null }
      })
    );
    updateGroup(group.id, {
      action: "assign_leaders",
      leader_ids: [...selectedIdUserMap.keys()]
    }).then(() => {
      if (waitingSnackbar)
        dispatch(closeSnackbar(waitingSnackbar.notification.key));
      dispatch(
        enqueueSuccessSnackbar({
          message: t("messages.successMsg")
        })
      );
      goBack();
    });
  };

  const cancel = () => {
    goBack();
  };

  return (
    <AdminPageLayout
      title={
        <Breadcrumb routes={["Groups", group ? group.name : "", "Leaders"]} />
      }
      onGoBackClick={goBack}
    >
      <Box display="flex" my={4} flexDirection="column" alignItems="stretch">
        <Filters onFilter={filters => applyFilters(filters)} group={group} />

        {loading ? (
          <Box pt={5} pb={15}>
            <LoadingSpinner />
          </Box>
        ) : (
          <>
            <DualList
              renderList={(items, DualListItem) => (
                <Box flex={1} minHeight={460} mt={3}>
                  <AutoSizer>
                    {({ height, width }) => (
                      <List
                        className="List"
                        height={height}
                        itemCount={items.length}
                        itemSize={89}
                        width={width}
                      >
                        {({ index, style }) => {
                          return (
                            <div style={style}>
                              {DualListItem(items[index])}
                            </div>
                          );
                        }}
                      </List>
                    )}
                  </AutoSizer>
                </Box>
              )}
              disabled={isUpdating}
              onAddAll={handleAddAll}
              removeAllButtonText={t("list.removeAll")}
              addAllButtonText={t("list.addAll")}
              onRemoveAll={handleRemoveAll}
              onAddToSelectedList={handleAdd}
              onAddToUnselectedList={handleRemove}
              selectedListHeader={t("list.groupLeaders.header")}
              unselectedListHeader={t("list.availableEmployees.header")}
              selectedList={selectedList}
              unselectedList={unselectedList}
            />
          </>
        )}
        <Box mt={4} display="flex" justifyContent="flex-end">
          <ButtonGroup>
            <Button color="primary" onClick={cancel}>
              {t("cancel")}
            </Button>
            <Button variant="contained" color="primary" onClick={save}>
              {t("save")}
            </Button>
          </ButtonGroup>
        </Box>
      </Box>
    </AdminPageLayout>
  );
};

export default GroupLeaders;
